// React and core
import { useEffect, useState } from "react";

// Next.js navigation and image
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import NextImage from "next/image";

// Firebase auth
import { getAuth } from "firebase/auth";

// Mantine Core and hooks
import {
  Flex,
  Stack,
  Tooltip,
  UnstyledButton,
  rem,
  Image,
  Popover,
  Box,
  Text,
  Indicator,
  ScrollArea,
  Title,
  Group,
  Menu,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

// clsx
import cx from "clsx";

// Tabler icons
import {
  IconBell,
  IconBellFilled,
  IconHome2,
  IconX,
  IconEye,
  IconSettings,
  IconPlus,
  IconFilePlus,
  IconUserPlus,
  IconLifebuoy,
  IconLogout,
} from "@tabler/icons-react";

// Mantine Spotlight
import { Spotlight as MantineSpotlight } from "@mantine/spotlight";

// App constants and helpers
import { ActionNames } from "@/app/@types/enums";
import Logo from "@/app/_media/Logo.svg";
import classes from "./DesktopHeader.module.css";
import navItemConstants, {
  getSidebarSettings,
  isActiveRoute,
  isHiddenBySidebarSettings,
  sidebarShortcuts,
} from "../../constants/navItems";
import {
  hasNotification,
  orderNotifications,
  unreadNotificationLength,
} from "@/app/_helpers/notifications";

import { runBeforeRouteChange } from "@/app/_utils/navigationHookRegistry";
import ThemeToggle2 from "@/app/_components/ThemeToggle/ThemeToggle2";
import { useSupportContext } from "@/app/_providers/SupportProvider/SupportProvider";

// App hooks
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSubscriptionContext } from "@/app/_hooks/subscription/useSubscriptionContext";
import { checkFeatureFlag } from "@/app/_config/featureFlags";
import useCanPerformAction from "@/app/_hooks/useCanPerformAction";
import useActiveJobLimit from "@/app/_hooks/jobs/useActiveJobLimit";
import { useNotificationsContext } from "@/app/_providers/NotificationsProvider/NotificationsProvider";
import useConversations from "@/app/_hooks/messages/useConversations";
import useTotalUnreadCount from "@/app/_hooks/messages/useTotalUnreadCount";

// App UI components
import UserNote from "@/app/_components/UserNote/UserNote";
import TeamMemberAvatar from "@/app/_components/TeamMemberAvatar/TeamMemberAvatar";

interface NavbarLinkProps {
  icon: typeof IconHome2;
  label: string;
  active?: boolean;
  onClick?(): void;
  disabled?: boolean;
  walkthroughTarget?: string;
}

function NavbarLink({
  icon: Icon,
  label,
  active,
  onClick,
  disabled,
  walkthroughTarget,
}: NavbarLinkProps) {
  return (
    <Tooltip
      label={label}
      withArrow
      position="right"
      transitionProps={{ duration: 0 }}
    >
      <UnstyledButton
        onClick={onClick}
        data-active={active || undefined}
        pl={label === "Logout" ? rem(5) : 0}
        className={cx(
          classes.link,
          label,
          walkthroughTarget && walkthroughTarget,
          disabled && classes.disabled,
          "nav-item"
        )}
        disabled={disabled}
      >
        <Icon size={24} stroke={1.5} />
      </UnstyledButton>
    </Tooltip>
  );
}

const DesktopHeader = () => {
  const pathname = usePathname();
  const user = useUserContext();
  const { notifications, markAllAsRead } = useNotificationsContext();
  const theme = useMantineTheme();
  const router = useRouter();
  const auth = getAuth();
  const canPerformAction = useCanPerformAction();
  const subscription = useSubscriptionContext();
  // Inventory + Expenses are a Standard+ feature (hidden on Lite).
  const isLite = subscription.level === "lite";
  // Supplies feature (inventory/expenses) is a restricted rollout.
  const canUseSupplies = checkFeatureFlag("MM_SUPPLIES_ENABLED", user.uid);
  const {
    data: activeJobLimit,
    isLoading: activeJobLimitLoading,
    isFetching: activeJobLimitFetching,
    isPending: activeJobLimitPending,
  } = useActiveJobLimit();
  const [navItems, setNavItems] = useState(navItemConstants);
  const { conversations } = useConversations();
  const totalUnread = useTotalUnreadCount(conversations);
  const messagingEnabled = user.settings.enableClientMessaging;

  const [isMarkAllAsReadHovered, setIsMarkAllAsReadHovered] = useState(false);

  const [
    isNotificationOpen,
    { close: closeNotification, toggle: toggleNotification },
  ] = useDisclosure(false);

  const { open: openSupport } = useSupportContext();
  const sidebarSettings = getSidebarSettings(user.uid);

  const links = navItems
    .filter((link) => canPerformAction(link.actionName))
    .filter((link) => link.label !== "Messages" || messagingEnabled)
    .filter(
      (link) =>
        !(
          isLite &&
          (link.label === "Inventory" || link.label === "Expenses")
        )
    )
    .filter(
      (link) =>
        canUseSupplies ||
        (link.label !== "Inventory" && link.label !== "Expenses")
    )
    // Modules hidden for this tenant (see TENANT_SIDEBAR).
    .filter((link) => !isHiddenBySidebarSettings(link.label, sidebarSettings))
    .map((link) => {
      const navLink = (
        <NavbarLink
          disabled={link.disabled}
          label={link.label}
          icon={link.icon}
          key={link.label}
          active={link.path ? isActiveRoute(link.path, pathname) : false}
          walkthroughTarget={link.walkthroughTarget}
          onClick={() => {
            if (link.path) {
              runBeforeRouteChange(() => router.push(link.path));
            }

            if (link.label === "Logout") {
              auth.signOut();
            }
          }}
        />
      );

      if (link.label === "Messages" && totalUnread > 0) {
        return (
          <Indicator
            key={link.label}
            color="red"
            processing
            size={10}
            offset={12}
            position="top-end"
          >
            {navLink}
          </Indicator>
        );
      }

      return navLink;
    });

  // Settings pages pinned to the sidebar for this tenant (Price List,
  // Payments, Support). No active state: they live under /settings, which
  // has no sidebar entry of its own to compete with.
  const shortcutLinks = sidebarShortcuts
    .filter((shortcut) => sidebarSettings?.[shortcut.key] === true)
    .filter((shortcut) => canPerformAction(shortcut.actionName))
    .map((shortcut) => (
      <NavbarLink
        key={shortcut.label}
        label={shortcut.label}
        icon={shortcut.icon}
        onClick={() => {
          if (shortcut.path) {
            runBeforeRouteChange(() => router.push(shortcut.path));
          } else {
            openSupport();
          }
        }}
      />
    ));

  useEffect(() => {
    if (activeJobLimit) {
      const updatedNavItems = navItemConstants.map((item) => {
        if (item.actionName === ActionNames.CreateJobs) {
          if (activeJobLimit.isOverLimit) {
            return {
              ...item,
              disabled: true,
              label: `${activeJobLimit.activeJobs}/${activeJobLimit.limit} Active Jobs`,
            };
          } else {
            return {
              ...item,
              disabled: false,
              label: "New Job", // Reset to original label
            };
          }
        }
        return item;
      });

      setNavItems(updatedNavItems);
    }
  }, [
    activeJobLimit.activeJobs,
    activeJobLimitLoading,
    activeJobLimitFetching,
    activeJobLimitPending,
  ]);

  return (
    <Box component="header" h="100%">
      <Flex
        component="nav"
        w={80}
        h="100%"
        pt="md"
        direction="column"
        align="center"
        bg={"var(--primary-color"}
        style={{ overflow: "hidden" }}
      >
        <Stack px="md" style={{ flexShrink: 0 }}>
          <Link href={`/`}>
            <Image
              component={NextImage}
              src={Logo}
              alt="MountMonitor"
              height={64}
              w="auto"
            />
          </Link>
        </Stack>

        <Stack className={classes.navScroll} flex={1} mt={25} gap="sm">
          <Popover
            opened={isNotificationOpen}
            position="left"
            closeOnClickOutside={false}
            offset={{ mainAxis: 8, crossAxis: 125 }}
            withArrow
            trapFocus
            shadow="xl"
          >
            <Tooltip
              label="Notifications"
              position="right"
              transitionProps={{ duration: 0 }}
              withArrow
            >
              <Indicator
                offset={14}
                color="red"
                processing={hasNotification(notifications)}
                disabled={!hasNotification(notifications)}
                label={
                  <Text fw={700} size="xs">
                    {unreadNotificationLength(notifications)}
                  </Text>
                }
                size={16}
                position="top-end"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  toggleNotification();
                }}
              >
                <Popover.Target>
                  <Box className={classes.bellWrapper}>
                    <UnstyledButton
                      onClick={() => {
                        toggleNotification();
                      }}
                      className={classes.link}
                    >
                      {hasNotification(notifications) ? (
                        <IconBellFilled size={24} stroke={1.5} />
                      ) : (
                        <IconBell size={24} stroke={1.3} />
                      )}
                    </UnstyledButton>
                  </Box>
                </Popover.Target>
              </Indicator>
            </Tooltip>
            <Popover.Dropdown>
              <Stack>
                <Stack gap={0}>
                  <Group justify="space-between">
                    <Title order={3}>Notifications</Title>
                    <UnstyledButton
                      style={{ cursor: "pointer" }}
                      onClick={() => closeNotification()}
                    >
                      <IconX />
                    </UnstyledButton>
                  </Group>
                  {hasNotification(notifications) && (
                    <Box
                      w="fit-content"
                      onMouseOver={() => {
                        setIsMarkAllAsReadHovered(true);
                      }}
                      onMouseOut={() => {
                        setIsMarkAllAsReadHovered(false);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {isMarkAllAsReadHovered ? (
                        <Group gap={5} onClick={markAllAsRead}>
                          <Text size="xs" fw={500} c={theme.colors.green[7]}>
                            Mark all as read
                          </Text>
                          <IconEye
                            cursor="pointer"
                            size={16}
                            color={theme.colors.green[7]}
                            stroke={2}
                          />
                        </Group>
                      ) : (
                        <Text
                          size="xs"
                          fw={500}
                          style={{ cursor: "pointer" }}
                          onClick={() => markAllAsRead()}
                        >
                          Mark all as read
                        </Text>
                      )}
                    </Box>
                  )}
                </Stack>
                <ScrollArea w={500} h={350}>
                  <Stack gap="xs">
                    {notifications.length > 0 ? (
                      orderNotifications(notifications).map((notification) => (
                        <UserNote
                          closeNotification={closeNotification}
                          notification={notification}
                          key={notification.id}
                        />
                      ))
                    ) : (
                      <Box>No notifications</Box>
                    )}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Popover.Dropdown>
          </Popover>
          {links}
          {shortcutLinks}
          {/* <Divider color="White" />
          <Box h="1" pos="relative">

            <Box
              h="1"
              w={80}
              top={-13}
              left={-20}
              bg="white"
              pos="absolute"
            ></Box>
          </Box> */}
        </Stack>

        <Stack
          gap="md"
          align="center"
          pos="relative"
          w="100%"
          py="md"
          style={{ flexShrink: 0 }}
        >
          {canPerformAction(ActionNames.ViewSettings) ? (
            // Has settings access: cog goes straight to the settings page (where
            // the theme/report/logout controls already live).
            <NavbarLink
              label="Settings"
              icon={IconSettings}
              onClick={() =>
                runBeforeRouteChange(() => router.push("/settings"))
              }
            />
          ) : (
            // No settings access: the cog still opens a small menu so they can
            // change theme, report an issue, and log out.
            <Popover position="right-end" withArrow shadow="md" width={200}>
              <Popover.Target>
                <Box>
                  <NavbarLink label="Menu" icon={IconSettings} />
                </Box>
              </Popover.Target>
              <Popover.Dropdown p="xs">
                <Stack gap="xs">
                  <UnstyledButton
                    onClick={() => openSupport()}
                    px="xs"
                    py={4}
                  >
                    <Group gap="xs">
                      <IconLifebuoy size={16} />
                      <Text size="sm">Support</Text>
                    </Group>
                  </UnstyledButton>
                  <UnstyledButton
                    onClick={() => auth.signOut()}
                    px="xs"
                    py={4}
                  >
                    <Group gap="xs">
                      <IconLogout size={16} />
                      <Text size="sm">Logout</Text>
                    </Group>
                  </UnstyledButton>
                  <Box px="xs">
                    <ThemeToggle2 />
                  </Box>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          )}
          <Link
            href={
              canPerformAction(ActionNames.ViewSettings) ? "/settings" : "/jobs"
            }
          >
            <TeamMemberAvatar teamMember={user.details} />
          </Link>
        </Stack>
      </Flex>
    </Box>
  );
};

export default DesktopHeader;
